import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { 
  X, 
  Save,
  Plus,
  Trash2,
  AlertCircle,
  CheckCircle,
  Loader2
} from 'lucide-react'
import { clsx } from 'clsx'

interface ExtractedPerson {
  name: string
  role?: string
  attitude?: string
  title?: string
  department?: string
}

interface ExtractedFact {
  category: string
  content: string
}

interface EvidenceData {
  id: string
  contentSummary?: string
  extractedTags?: {
    persons?: ExtractedPerson[]
    facts?: ExtractedFact[]
    riskFlags?: string[]
  }
  demandLayer?: string
  signalStrength?: string
  signalType?: string
}

interface EvidenceEditModalProps {
  isOpen: boolean
  onClose: () => void
  evidence: EvidenceData | null
  onSave: (evidenceId: string, updates: {
    contentSummary?: string
    persons?: ExtractedPerson[]
    facts?: ExtractedFact[]
    riskFlags?: string[]
    demandLayer?: string
    signalStrength?: string
  }) => Promise<void>
}

const ROLE_OPTIONS = ['EB', 'TB', 'UB', 'Coach', 'Other']
const ATTITUDE_OPTIONS = ['支持', '中立', '反对', '未知']
const FACT_CATEGORIES = ['Budget', 'Timeline', 'Competitor', 'Risk', 'Decision', 'Technical', 'Other']
const DEMAND_LAYERS = ['solution', 'organization', 'personal']
const SIGNAL_STRENGTHS = ['strong', 'medium', 'weak']

export default function EvidenceEditModal({
  isOpen,
  onClose,
  evidence,
  onSave
}: EvidenceEditModalProps) {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  
  // 可编辑的状态
  const [summary, setSummary] = useState('')
  const [persons, setPersons] = useState<ExtractedPerson[]>([])
  const [facts, setFacts] = useState<ExtractedFact[]>([])
  const [riskFlags, setRiskFlags] = useState<string[]>([])
  const [demandLayer, setDemandLayer] = useState('solution')
  const [signalStrength, setSignalStrength] = useState('weak')

  // 初始化表单数据
  useEffect(() => {
    if (evidence && isOpen) {
      setSummary(evidence.contentSummary || '')
      setPersons(evidence.extractedTags?.persons || [])
      setFacts(evidence.extractedTags?.facts || [])
      setRiskFlags(evidence.extractedTags?.riskFlags || [])
      setDemandLayer(evidence.demandLayer?.toLowerCase() || 'solution')
      setSignalStrength(evidence.signalStrength?.toLowerCase() || 'weak')
      setError(null)
      setSuccess(false)
    }
  }, [evidence, isOpen])

  // 添加人物
  const addPerson = () => {
    setPersons([...persons, { name: '', role: 'Other', attitude: '未知' }])
  }

  // 更新人物
  const updatePerson = (index: number, field: keyof ExtractedPerson, value: string) => {
    const updated = [...persons]
    updated[index] = { ...updated[index], [field]: value }
    setPersons(updated)
  }

  // 删除人物
  const removePerson = (index: number) => {
    setPersons(persons.filter((_, i) => i !== index))
  }

  // 添加事实
  const addFact = () => {
    setFacts([...facts, { category: 'Other', content: '' }])
  }

  // 更新事实
  const updateFact = (index: number, field: keyof ExtractedFact, value: string) => {
    const updated = [...facts]
    updated[index] = { ...updated[index], [field]: value }
    setFacts(updated)
  }

  // 删除事实
  const removeFact = (index: number) => {
    setFacts(facts.filter((_, i) => i !== index))
  }

  // 添加风险标记
  const [newRiskFlag, setNewRiskFlag] = useState('')
  const addRiskFlag = () => {
    if (newRiskFlag.trim()) {
      setRiskFlags([...riskFlags, newRiskFlag.trim()])
      setNewRiskFlag('')
    }
  }

  // 删除风险标记
  const removeRiskFlag = (index: number) => {
    setRiskFlags(riskFlags.filter((_, i) => i !== index))
  }

  // 保存
  const handleSave = async () => {
    if (!evidence) return
    
    setIsLoading(true)
    setError(null)
    
    try {
      await onSave(evidence.id, {
        contentSummary: summary,
        persons: persons.filter(p => p.name.trim()),
        facts: facts.filter(f => f.content.trim()),
        riskFlags,
        demandLayer,
        signalStrength
      })
      
      setSuccess(true)
      setTimeout(() => {
        onClose()
      }, 1500)
    } catch (err) {
      setError(err instanceof Error ? err.message : '保存失败，请重试')
    } finally {
      setIsLoading(false)
    }
  }

  if (!isOpen || !evidence) return null

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        {/* 背景遮罩 */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0 bg-black/50 backdrop-blur-sm"
          onClick={onClose}
        />

        {/* 弹窗内容 */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="relative w-full max-w-3xl max-h-[90vh] bg-white rounded-2xl shadow-2xl overflow-hidden"
        >
          {/* 头部 */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">纠正证据信息</h2>
              <p className="text-sm text-gray-500">修正AI抽取的信息，提高诊断准确性</p>
            </div>
            <button
              onClick={onClose}
              disabled={isLoading}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* 成功提示 */}
          {success && (
            <div className="mx-6 mt-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <span className="text-green-700">保存成功！正在更新诊断结果...</span>
            </div>
          )}

          {/* 错误提示 */}
          {error && (
            <div className="mx-6 mt-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-red-600" />
              <span className="text-red-700">{error}</span>
            </div>
          )}

          {/* 内容区域 */}
          <div className="p-6 overflow-y-auto max-h-[60vh] space-y-6">
            {/* 摘要 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">内容摘要</label>
              <textarea
                value={summary}
                onChange={(e) => setSummary(e.target.value)}
                rows={2}
                className="w-full px-4 py-2 border border-gray-300 rounded-xl resize-none focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="一句话总结证据核心内容..."
              />
            </div>

            {/* 信号强度和需求层级 */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">信号强度</label>
                <select
                  value={signalStrength}
                  onChange={(e) => setSignalStrength(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  {SIGNAL_STRENGTHS.map(s => (
                    <option key={s} value={s}>
                      {s === 'strong' ? '强信号' : s === 'medium' ? '中信号' : '弱信号'}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">需求层级</label>
                <select
                  value={demandLayer}
                  onChange={(e) => setDemandLayer(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  {DEMAND_LAYERS.map(l => (
                    <option key={l} value={l}>
                      {l === 'solution' ? '方案层' : l === 'organization' ? '组织层' : '个人层'}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* 关键人物 */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-gray-700">关键人物</label>
                <button
                  onClick={addPerson}
                  className="flex items-center gap-1 text-xs text-primary-600 hover:text-primary-700"
                >
                  <Plus className="w-3 h-3" />
                  添加人物
                </button>
              </div>
              <div className="space-y-2">
                {persons.map((person, index) => (
                  <div key={index} className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                    <input
                      type="text"
                      value={person.name}
                      onChange={(e) => updatePerson(index, 'name', e.target.value)}
                      placeholder="姓名"
                      className="flex-1 px-3 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                    <select
                      value={person.role || 'Other'}
                      onChange={(e) => updatePerson(index, 'role', e.target.value)}
                      className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                    >
                      {ROLE_OPTIONS.map(r => (
                        <option key={r} value={r}>{r}</option>
                      ))}
                    </select>
                    <select
                      value={person.attitude || '未知'}
                      onChange={(e) => updatePerson(index, 'attitude', e.target.value)}
                      className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                    >
                      {ATTITUDE_OPTIONS.map(a => (
                        <option key={a} value={a}>{a}</option>
                      ))}
                    </select>
                    <button
                      onClick={() => removePerson(index)}
                      className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
                {persons.length === 0 && (
                  <p className="text-sm text-gray-400 py-2">暂无识别到的人物，点击"添加人物"手动添加</p>
                )}
              </div>
            </div>

            {/* 业务事实 */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-gray-700">业务事实</label>
                <button
                  onClick={addFact}
                  className="flex items-center gap-1 text-xs text-primary-600 hover:text-primary-700"
                >
                  <Plus className="w-3 h-3" />
                  添加事实
                </button>
              </div>
              <div className="space-y-2">
                {facts.map((fact, index) => (
                  <div key={index} className="flex items-start gap-2 p-3 bg-gray-50 rounded-lg">
                    <select
                      value={fact.category}
                      onChange={(e) => updateFact(index, 'category', e.target.value)}
                      className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                    >
                      {FACT_CATEGORIES.map(c => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                    <input
                      type="text"
                      value={fact.content}
                      onChange={(e) => updateFact(index, 'content', e.target.value)}
                      placeholder="事实内容"
                      className="flex-1 px-3 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                    <button
                      onClick={() => removeFact(index)}
                      className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
                {facts.length === 0 && (
                  <p className="text-sm text-gray-400 py-2">暂无识别到的事实，点击"添加事实"手动添加</p>
                )}
              </div>
            </div>

            {/* 风险标记 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">风险标记</label>
              <div className="flex flex-wrap gap-2 mb-2">
                {riskFlags.map((flag, index) => (
                  <span
                    key={index}
                    className="flex items-center gap-1 px-2 py-1 bg-red-50 text-red-700 rounded-full text-sm"
                  >
                    {flag}
                    <button
                      onClick={() => removeRiskFlag(index)}
                      className="p-0.5 hover:bg-red-100 rounded-full"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={newRiskFlag}
                  onChange={(e) => setNewRiskFlag(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && addRiskFlag()}
                  placeholder="输入风险标记后回车添加..."
                  className="flex-1 px-3 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
                <button
                  onClick={addRiskFlag}
                  className="px-3 py-1.5 bg-gray-100 text-gray-600 rounded-lg text-sm hover:bg-gray-200"
                >
                  添加
                </button>
              </div>
            </div>
          </div>

          {/* 底部操作栏 */}
          <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100 bg-gray-50">
            <div className="text-xs text-gray-500">
              <AlertCircle className="w-3 h-3 inline mr-1" />
              修正后系统将重新计算诊断结果
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={onClose}
                disabled={isLoading}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 font-medium"
              >
                取消
              </button>
              <button
                onClick={handleSave}
                disabled={isLoading || success}
                className={clsx(
                  'px-6 py-2 rounded-lg font-medium flex items-center gap-2 transition-colors',
                  isLoading || success
                    ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                    : 'bg-primary-600 text-white hover:bg-primary-700'
                )}
              >
                {isLoading && <Loader2 className="w-4 h-4 animate-spin" />}
                {success && <CheckCircle className="w-4 h-4" />}
                <Save className="w-4 h-4" />
                保存修改
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  )
}

